import { ApolloError } from '@apollo/client';
import { FormLeavingListener } from '@exam-center/components';
import { useExamCenter } from '@exam-center/exam-center-details';
import {
  useCreateExamCenterProctorRequestMutation,
  useSubmitExamCenterProctorRequestForReviewMutation,
} from '@exam-center/graphql';
import {
  ALLOW_FILE_TYPES,
  DownloadProctorAgreementFormHelperText,
  PROCTOR_REQUEST_TYPE_OPTIONS,
  ProctorRequestFormFields,
  ProctorRequestSubmissionModal,
  ProctorType,
  mapExamCenterProctorRecordErrorCodeToFormError,
  useUploadProctorRequestFiles,
} from '@exam-center/proctor-request-management';
import { useNavigate } from '@exam-center/router';
import { Button, Flex, Form } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { ENGLISH_TITLE_OPTIONS, THAI_TITLE_OPTIONS } from '@shared/utils';
import { useEffect } from 'react';

import {
  useProctorRequestCreationForm,
  type ProctorRequestCreationFormInput,
} from '../../providers';
import { PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME } from '../../providers/proctor-request-creation-form-provider/constants';

import { createExamCenterProctorRequestPayload } from './utils';

export default function ProctorRequestCreationFormLogic() {
  const { showPromiseSnackbar, showSnackbar } = useSnackbar();
  const [createExamCenterProctorRequest, { loading: isCreatingRequest }] =
    useCreateExamCenterProctorRequestMutation();
  const [submitExamCenterProctorRequestForReview, { loading: isSubmittingRequest }] =
    useSubmitExamCenterProctorRequestForReviewMutation();

  const [isProctorRequestSubmissionModalOpen, proctorRequestSubmissionModalHandlers] =
    useDisclosure();
  const { open: openProctorRequestSubmissionModal, close: closeProctorRequestSubmissionModal } =
    proctorRequestSubmissionModalHandlers;

  const { examCenter } = useExamCenter();
  const navigate = useNavigate();

  const formContext = useProctorRequestCreationForm();

  const { handleUpload } = useUploadProctorRequestFiles();

  const handleCreateExamCenterProctorRequest = async (data: ProctorRequestCreationFormInput) => {
    const { agreementForms, identifications } = data.document;

    const agreementFileNames = (await handleUpload(agreementForms)) ?? [];
    const identificationFileNames = (await handleUpload(identifications)) ?? [];

    const payload = createExamCenterProctorRequestPayload(
      data,
      agreementFileNames,
      identificationFileNames,
      examCenter.code,
    );

    return await createExamCenterProctorRequest({
      variables: {
        createExamCenterProctorRequestInput: payload,
      },
    });
  };

  const handleSaveDraft = async (data: ProctorRequestCreationFormInput) => {
    try {
      const { data: createExamCenterProctorRequestData } =
        await handleCreateExamCenterProctorRequest(data);

      if (
        createExamCenterProctorRequestData?.ecCreateExamCenterProctorRequest
          ?.examCenterProctorRequest?.id
      ) {
        return navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
          params: {
            examCenterCode: examCenter.code,
            requestId:
              createExamCenterProctorRequestData.ecCreateExamCenterProctorRequest
                .examCenterProctorRequest.id,
          },
        });
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
        const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
        const errorCodes = extensionDetails.map((detail) => detail.code);
        const formErrors = mapExamCenterProctorRecordErrorCodeToFormError(errorCodes);

        for (const formError of formErrors) {
          formContext.setError(formError.fieldName, {
            type: 'manual',
            message: formError.message,
          });
        }
      }
      throw error;
    }
  };

  const handleDraftClick = async () => {
    formContext.handleSubmit((data) => {
      showPromiseSnackbar({
        message: {
          pending: 'กำลังบันทึก',
          success: 'บันทึกแบบร่างเรียบร้อยแล้ว',
          error: 'ไม่สามารถบันทึกแบบร่างได้',
        },
        promise: () => handleSaveDraft(data),
      });
    })();
  };

  const handleSaveDraftAndSubmit = async (input: ProctorRequestCreationFormInput) => {
    try {
      const { data: createExamCenterProctorRequestData } =
        await handleCreateExamCenterProctorRequest(input);

      if (
        createExamCenterProctorRequestData?.ecCreateExamCenterProctorRequest
          ?.examCenterProctorRequest?.id
      ) {
        await submitExamCenterProctorRequestForReview({
          variables: {
            submitExamCenterProctorRequestInput: {
              id: createExamCenterProctorRequestData.ecCreateExamCenterProctorRequest
                .examCenterProctorRequest.id,
            },
          },
          onCompleted: () => {
            showSnackbar({
              message: 'ส่งคำขออนุมัติเรียบร้อยแล้ว',
              variant: 'success',
            });

            navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
              params: {
                examCenterCode: examCenter.code,
                requestId:
                  createExamCenterProctorRequestData.ecCreateExamCenterProctorRequest
                    .examCenterProctorRequest.id,
              },
            });
          },
          onError: () => {
            showSnackbar({
              message: 'ส่งคำขออนุมัติไม่สำเร็จ',
              variant: 'error',
            });

            navigate(`/:examCenterCode/exam-proctors/requests/:requestId`, {
              params: {
                examCenterCode: examCenter.code,
                requestId:
                  createExamCenterProctorRequestData.ecCreateExamCenterProctorRequest
                    .examCenterProctorRequest.id,
              },
            });
          },
        });
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        const graphQLError = error?.graphQLErrors?.[0]?.extensions?.details;
        const extensionDetails = Array.isArray(graphQLError) ? graphQLError : [graphQLError];
        const errorCodes = extensionDetails.map((detail) => detail.code);
        const formErrors = mapExamCenterProctorRecordErrorCodeToFormError(errorCodes);

        for (const formError of formErrors) {
          formContext.setError(formError.fieldName, {
            type: 'manual',
            message: formError.message,
          });
        }
      }
      throw error;
    } finally {
      closeProctorRequestSubmissionModal();
    }
  };

  const handleAcceptSubmit = async () => {
    await formContext.handleSubmit(handleSaveDraftAndSubmit)();
  };

  const handleSubmit = async () => {
    const isValid = await formContext.trigger();

    if (!isValid) return;

    openProctorRequestSubmissionModal();
  };

  const shouldDisplayEndDate =
    formContext.watch(
      PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.termination.isAssigned,
    ) === 'true';

  useEffect(() => {
    if (!shouldDisplayEndDate) {
      formContext.setValue(
        PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.termination.endedAt,
        null,
      );
    }
  }, [shouldDisplayEndDate, formContext]);

  const minEndAtDate = formContext.watch(
    PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.startedAt,
  );

  const isSubmitRequestLoading =
    formContext.formState.isSubmitting || isCreatingRequest || isSubmittingRequest;

  return (
    <>
      <Form
        sx={(theme) => ({
          p: theme.spacing(6),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(6),
        })}
      >
        <ProctorRequestFormFields
          fields={{
            proctorType: {
              mode: 'input',
              name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.proctorType,
              slotProps: { options: PROCTOR_REQUEST_TYPE_OPTIONS },
            },
            personal: {
              idNumber: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.idNumber,
              },
              titleTh: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.titleTh,
                slotProps: { options: THAI_TITLE_OPTIONS },
              },
              firstNameTh: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.firstNameTh,
              },
              lastNameTh: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.lastNameTh,
              },
              titleEn: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.titleEn,
                slotProps: { options: ENGLISH_TITLE_OPTIONS },
              },
              firstNameEn: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.firstNameEn,
              },
              lastNameEn: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.lastNameEn,
              },
              email: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.email,
              },
              phoneNumber: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.phoneNumber,
              },
              address: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.personal.address,
              },
            },
            employeeInfo: {
              code: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.code,
              },
              position: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.position,
              },
              department: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.department,
              },
              startedAt: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.startedAt,
              },
              isTerminationAssign: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.termination.isAssigned,
              },
              endedAt: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.employeeInfo.termination.endedAt,
                slotProps: {
                  minDate: minEndAtDate,
                },
              },
            },
            documents: {
              agreementForms: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.document.agreementForms,
                slotProps: {
                  maxFiles: 4,
                  accept: ALLOW_FILE_TYPES,
                  helperText: (
                    <DownloadProctorAgreementFormHelperText
                      proctorType={ProctorType.TYPE_ONE_AND_TWO}
                    />
                  ),
                },
              },
              identifications: {
                mode: 'input',
                name: PROCTOR_REQUEST_CREATION_FORM_FIELD_NAME.document.identifications,
                slotProps: {
                  maxFiles: 4,
                  accept: ALLOW_FILE_TYPES,
                },
              },
            },
          }}
          options={{
            shouldShowEndedAtDateDisplay: shouldDisplayEndDate,
            hideIdentificationFiles: false,
          }}
        />
        <Flex sx={(theme) => ({ gap: theme.spacing(2.5), ml: 'auto' })}>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={handleDraftClick}
            disabled={isSubmitRequestLoading}
          >
            บันทึกแบบร่าง
          </Button>
          <Button
            size='large'
            color='primary'
            onClick={handleSubmit}
            disabled={isSubmitRequestLoading}
          >
            ส่งคำขออนุมัติ
          </Button>
        </Flex>
      </Form>
      <FormLeavingListener onSubmit={handleDraftClick} />
      <ProctorRequestSubmissionModal
        open={isProctorRequestSubmissionModalOpen}
        onClose={closeProctorRequestSubmissionModal}
        onAccept={handleAcceptSubmit}
        onDiscard={closeProctorRequestSubmissionModal}
        loading={formContext.formState.isSubmitting}
      />
    </>
  );
}
